import { defineStore } from 'pinia';
import axios from 'axios';
import type {
  EducationEntry,
  SubmitFilesSection,
  SubmitFormData,
  SubmitSectionPayload,
  ValidationError,
} from '../types';

axios.defaults.withCredentials = true;
const API = import.meta.env.VITE_API || '/api';

export interface FormValidation {
  isValid: boolean;
  errors: ValidationError[];
}

export const useForm = defineStore('form', {
  state: () => ({ 
    staffId: 0, 
    status: 'draft', 
    sections: {} as Record<string, SubmitSectionPayload>, 
    saving: false, 
    lastSaved: null as null | number,
    errors: [] as ValidationError[],
    submitting: false,
    submitError: null as string | null
  }),
  
  getters: {
    hasErrors: (state) => state.errors.length > 0,
    isSubmittable: (state) => {
      return state.status === 'draft' || state.status === 'in_progress' || state.status === 'submitted';
    }
  },

  actions: {
    async bootstrap() {
      try {
        const { data } = await axios.get(`${API}/form/current`, { withCredentials: true });
        this.staffId = data.id; 
        this.status = data.status; 
        this.sections = data.sections || {};
        this.clearErrors();
      } catch (error: any) {
        // console.error('Failed to bootstrap form:', error);
        throw new Error(error.response?.data?.error || 'Failed to load form data');
      }
    },

    isBlankValue(value: unknown): boolean {
      if (value === null || value === undefined) {
        return true;
      }

      if (typeof value === 'string') {
        return value.trim() === '';
      }

      return false;
    },

    normalizeEducationEntry(entry: Partial<EducationEntry> & Record<string, unknown>): EducationEntry {
      const rawYear = entry.year ?? entry.graduation_year ?? entry.end_year ?? entry.start_year ?? '';
      const startYear = entry.start_year ?? '';
      const endYear = entry.end_year ?? '';

      return {
        degree: typeof entry.degree === 'string' ? entry.degree : '',
        university: typeof entry.university === 'string' ? entry.university : '',
        year: rawYear === null || rawYear === undefined ? '' : (rawYear as number | string),
        graduation_year: entry.graduation_year as number | string | undefined,
        country: typeof entry.country === 'string' ? entry.country : '',
        field_of_study: typeof entry.field_of_study === 'string' ? entry.field_of_study : '',
        gpa: typeof entry.gpa === 'string' ? entry.gpa : '',
        start_year: startYear === null || startYear === undefined ? '' : (startYear as number | string),
        end_year: endYear === null || endYear === undefined ? '' : (endYear as number | string),
      };
    },

    isEmptyEducationEntry(entry: Partial<EducationEntry> & Record<string, unknown>): boolean {
      const normalized = this.normalizeEducationEntry(entry);

      return [
        normalized.degree,
        normalized.university,
        normalized.country,
        normalized.year,
        normalized.graduation_year,
        normalized.start_year,
        normalized.end_year,
      ].every((value) => this.isBlankValue(value));
    },

    validatePersonalInfo(personalInfo: SubmitFormData['personal_info']): ValidationError[] {
      const errors: ValidationError[] = [];
      
      if (!personalInfo.name_en?.trim()) {
        errors.push({ field: 'name_en', message: 'English name is required' });
      }
      
      if (!personalInfo.title_en?.trim()) {
        errors.push({ field: 'title_en', message: 'English title is required' });
      }
      
      if (!personalInfo.gender) {
        errors.push({ field: 'gender', message: 'Gender is required' });
      }
      
      if (!personalInfo.date_of_birth) {
        errors.push({ field: 'date_of_birth', message: 'Date of birth is required' });
      }
      
      if (!personalInfo.nationality?.trim()) {
        errors.push({ field: 'nationality', message: 'Nationality is required' });
      }
      
      if (!personalInfo.email?.trim()) {
        errors.push({ field: 'email', message: 'Email is required' });
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(personalInfo.email)) {
        errors.push({ field: 'email', message: 'Please enter a valid email address' });
      }
      
      if (!personalInfo.phone?.trim()) {
        errors.push({ field: 'phone', message: 'Phone number is required' });
      }
      
      return errors;
    },

    validateInstitutionInfo(institutionInfo: SubmitFormData['institution_info']): ValidationError[] {
      const errors: ValidationError[] = [];
      
      if (!institutionInfo.college_institute?.trim()) {
        errors.push({ field: 'college_institute', message: 'College/Institute is required' });
      }
      
      if (!institutionInfo.department?.trim()) {
        errors.push({ field: 'department', message: 'Department is required' });
      }
      
      if (!institutionInfo.specialization_en?.trim()) {
        errors.push({ field: 'specialization_en', message: 'Specialization is required' });
      }
      
      return errors;
    },

    validateFiles(files: SubmitFilesSection): ValidationError[] {
      const errors: ValidationError[] = [];
      
      // Only photo is required now, CV is no longer required
      if (!files.photo) {
        errors.push({ field: 'photo', message: 'Profile photo is required' });
      }
      
      return errors;
    },

    validateEducation(education: EducationEntry[]): ValidationError[] {
      const errors: ValidationError[] = [];
      const currentYear = new Date().getFullYear() + 10;
      const meaningfulEntries = (education || [])
        .map((entry, index) => ({
          index,
          entry: this.normalizeEducationEntry(entry as Partial<EducationEntry> & Record<string, unknown>),
        }))
        .filter(({ entry }) => !this.isEmptyEducationEntry(entry));
      
      if (meaningfulEntries.length === 0) {
        errors.push({ field: 'education', message: 'At least one education entry is required' });
        return errors;
      }
      
      meaningfulEntries.forEach(({ entry, index }) => {
        const startYear = entry.start_year ? Number(entry.start_year) : null;
        const endYear = entry.end_year ? Number(entry.end_year) : null;
        const graduationYear = entry.graduation_year ? Number(entry.graduation_year) : null;
        const primaryYear = entry.year ? Number(entry.year) : graduationYear ?? endYear ?? startYear;

        if (!entry.degree?.trim()) {
          errors.push({ field: `education.${index}.degree`, message: `Degree is required for education entry ${index + 1}` });
        }
        if (!entry.university?.trim()) {
          errors.push({ field: `education.${index}.university`, message: `University is required for education entry ${index + 1}` });
        }
        if (!primaryYear || primaryYear < 1900 || primaryYear > currentYear) {
          errors.push({ field: `education.${index}.year`, message: `Valid year is required for education entry ${index + 1}` });
        }
        if (startYear && endYear && startYear > endYear) {
          errors.push({ field: `education.${index}.end_year`, message: `End year must be after start year for education entry ${index + 1}` });
        }
        if (!entry.country?.trim()) {
          errors.push({ field: `education.${index}.country`, message: `Country is required for education entry ${index + 1}` });
        }
      });
      
      return errors;
    },

    validateFormData(formData: SubmitFormData): FormValidation {
      const allErrors: ValidationError[] = [];
      
      // Validate each section
      allErrors.push(...this.validatePersonalInfo(formData.personal_info || {}));
      allErrors.push(...this.validateInstitutionInfo(formData.institution_info || {}));
      allErrors.push(...this.validateFiles(formData.files || {}));
      allErrors.push(...this.validateEducation(formData.education || []));
      
      return {
        isValid: allErrors.length === 0,
        errors: allErrors
      };
    },

    async saveSection(key: string, payload: SubmitSectionPayload) {
      try {
        this.saving = true; 
        this.sections[key] = payload;
        await axios.put(`${API}/form/section/${key}`, payload, { 
          withCredentials: true,
          validateStatus: (s) => s < 500,
        });
        this.lastSaved = Date.now();
      } catch (error: any) {
        if (error.response?.status === 401) {
          return;
        }
        throw new Error(error.response?.data?.error || `Failed to save ${key} section`);
      } finally {
        this.saving = false; 
      }
    },

    async saveAllSections(formData: any) {
      try {
        this.saving = true;
        
        // Use batched save endpoint to save all sections in one request
        await axios.put(`${API}/form/sections/batch`, formData, { 
          withCredentials: true,
          validateStatus: (s) => s < 500,
        });
        this.lastSaved = Date.now();
        
        // Update local state
        Object.assign(this.sections, formData);
        
      } catch (error: any) {
        if (error.response?.status === 401) {
          return;
        }
        throw error;
      } finally {
        this.saving = false;
      }
    },

    async submit() { 
      try {
        this.submitting = true;
        this.submitError = null;
        
        // Validate form before submission
        const validation = this.validateFormData(this.sections);
        if (!validation.isValid) {
          this.errors = validation.errors;
          throw new Error('Please fix the validation errors before submitting');
        }
        
        await axios.post(`${API}/form/submit`, {}, { withCredentials: true }); 
        this.status = 'submitted';
        this.clearErrors();
        
        // Proactively log out on the client side after successful submission
        try {
          await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        } catch (_) {
          // Ignore logout errors; server also clears the cookie on submit
        }
        
      } catch (error: any) {
        // console.error('Failed to submit form:', error);
        this.submitError = error.response?.data?.error || error.message || 'Failed to submit form';
        throw error;
      } finally {
        this.submitting = false;
      }
    },

    clearErrors() {
      this.errors = [];
      this.submitError = null;
    },

    addError(field: string, message: string) {
      this.errors.push({ field, message });
    },

    removeError(field: string) {
      this.errors = this.errors.filter(error => error.field !== field);
    },

    getFieldErrors(field: string): string[] {
      return this.errors
        .filter(error => error.field === field)
        .map(error => error.message);
    }
  }
});
